import {
  Pipe,
  ChangeDetectorRef,
  PipeTransform,
  EventEmitter,
  OnDestroy,
  NgZone,
} from '@angular/core';
import moment from 'moment';
import { Subscription } from 'rxjs';

@Pipe({
    name: 'amCalendar', pure: false,
    standalone: false
})
export class AmCalendarPipe implements PipeTransform, OnDestroy {
  /**
   * Internal reference counter, so we can clean up when no instances are in use
   */
  private static refs = 0;
  private static timer: number | null = null;
  private static midnight: EventEmitter<Date> | null = null;
  private midnightSub: Subscription;

  constructor(private cdRef: ChangeDetectorRef, private ngZone: NgZone) {
    // using a single static timer for all instances of this pipe for performance reasons
    AmCalendarPipe.initTimer(ngZone);

    AmCalendarPipe.refs++;

    // values such as Today will need to be replaced with Yesterday after midnight,
    // so make sure we subscribe to an EventEmitter that we set up to emit at midnight
    this.midnightSub = AmCalendarPipe.midnight.subscribe(() => {
      this.ngZone.run(() => this.cdRef.markForCheck());
    });
  }

  transform(value: moment.MomentInput, ...args: any[]): any {
    let formats: any = null;
    let referenceTime: any = null;

    for (let i = 0, len = args.length; i < len; i++) {
      if (args[i] !== null) {
        if (typeof args[i] === 'object' && !moment.isMoment(args[i])) {
          formats = args[i];
        } else {
          referenceTime = moment(args[i]);
        }
      }
    }

    return moment(value).calendar(referenceTime, formats);
  }

  ngOnDestroy(): void {
    if (AmCalendarPipe.refs > 0) {
      AmCalendarPipe.refs--;
    }

    if (AmCalendarPipe.refs === 0) {
      AmCalendarPipe.removeTimer();
    }

    this.midnightSub.unsubscribe();
  }

  private static initTimer(ngZone: NgZone) {
    // initialize the timer
    if (!AmCalendarPipe.midnight) {
      AmCalendarPipe.midnight = new EventEmitter<Date>();
      if (typeof window !== 'undefined') {
        const timeToUpdate = AmCalendarPipe._getMillisecondsUntilUpdate();
        AmCalendarPipe.timer = ngZone.runOutsideAngular(() => {
          return window.setTimeout(() => {
            // emit the current date
            AmCalendarPipe.midnight.emit(new Date());

            // refresh the timer
            AmCalendarPipe.removeTimer();
            AmCalendarPipe.initTimer(ngZone);
          }, timeToUpdate);
        });
      }
    }
  }

  private static removeTimer() {
    if (AmCalendarPipe.timer) {
      window.clearTimeout(AmCalendarPipe.timer);
      AmCalendarPipe.timer = null;
      AmCalendarPipe.midnight = null;
    }
  }

  private static _getMillisecondsUntilUpdate() {
    const now = moment();
    const tomorrow = moment().startOf('day').add(1, 'days');
    const timeToMidnight = tomorrow.valueOf() - now.valueOf();
    return timeToMidnight + 1000; // 1 second after midnight
  }
}
